import { useForm, type FormMetadata } from '@conform-to/react';
import lodash from 'lodash';
import { useCallback, useId, useRef } from 'react';
import { typedjson } from 'remix-typedjson';
import { gtmCustomEvent } from './tracking/google-analytics';

const { throttle, debounce } = lodash;

export function getDomainUrl(request: Request) {
    const host = request.headers.get('X-Forwarded-Host') ?? request.headers.get('host') ?? new URL(request.url).host;
    const protocol = request.headers.get('X-Forwarded-Proto') ?? 'http';
    return `${protocol}://${host}`;
}

export function sizeDependent(
    input: string | { lg: string; sm: string },
    renderer: (text: string, visibilityClasses: string) => any,
    visibleClass: string = 'block',
) {
    const lg = typeof input === 'string' ? input : input.lg;
    const sm = typeof input === 'string' ? input : input.sm;

    return [ renderer(lg, `hidden lg:${visibleClass}`), renderer(sm, `${visibleClass} lg:hidden`) ];
}

export function validateEmail(email: unknown): email is string {
    return typeof email === 'string' && email.length > 3 && email.includes('@');
}

const DEFAULT_REDIRECT = '/';

/**
 * This should be used any time the redirect path is user-provided
 * (Like the query string on our login/signup pages). This avoids
 * open-redirect vulnerabilities.
 * @param {string} to The redirect destination
 * @param {string} defaultRedirect The redirect to use if the to is unsafe.
 */
export function safeRedirect(
    to: FormDataEntryValue | string | null | undefined,
    defaultRedirect: string = DEFAULT_REDIRECT,
) {
    if (!to || typeof to !== 'string') {
        return defaultRedirect;
    }

    if (!to.startsWith('/') || to.startsWith('//')) {
        return defaultRedirect;
    }

    return to;
}

export function PrefixWithEnv(input: string) {
    if (process.env.ENV === 'dev') {
        return `[DEV] ${input}`;
    } else if (process.env.ENV === 'stage') {
        return `[STAGE] ${input}`;
    }
    return input;
}

export function useThrottle<T extends any[]>(callback: (...args: T) => void, delay: number) {
    const throttled = useRef(throttle(callback, delay, { leading: true, trailing: true }));

    return useCallback((...args: T) => {
        throttled.current(...args);
    }, []);
}

export function wrapActions<T extends Record<string, () => Promise<Response>>>(actions: T) {
    return Object.fromEntries(
        Object.entries(actions).map(([ key, fn ]) => [ key, async () => {
            const response = await fn();

            // Extract existing JSON data from typedjson response
            const { status, headers } = response;
            const jsonData = await response.json();

            // Wrap the extracted JSON inside an object with the function name
            return typedjson({ [key]: jsonData }, { status, headers });
        } ])
    );
}

export function useNamedForm<Schema extends Record<string, any>, FormValue = Schema, FormError = string[]>(
    { name, ...options }: Omit<Parameters<typeof useForm<Schema, FormValue, FormError>>[0], 'id'> & { name: string },
): [FormMetadata<Schema, FormError>, ReturnType<FormMetadata<Schema, FormError>['getFieldset']>] {
    const id = useId();
    return useForm({
        ...options,
        id: `${name}_${id}`
    });
}

export function useTrackedForm<Schema extends Record<string, any>, FormValue = Schema, FormError = string[]>(
    { name, eventName, ...options }: Omit<Parameters<typeof useForm<Schema, FormValue, FormError>>[0], 'id'> & {
        name: string;
        eventName?: string;
    },
): [FormMetadata<Schema, FormError>, ReturnType<FormMetadata<Schema, FormError>['getFieldset']>] {
    const id = useId();
    return useForm({
        ...options,
        onSubmit: async (event, context) => {
            gtmCustomEvent(eventName || 'generate_lead', {
                form_name: name
            });
            options.onSubmit?.(event, context);
        },
        id: `${name}_${id}`
    });
}
